// @flow

import React, { Component } from 'react';
import { withRouter } from 'react-router';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, Row, Spinner, Table } from 'reactstrap';
import type { RouterHistory } from 'react-router';
import {
    fetchRestaurantActivityMetrics,
    formatCount,
    formatMetricValue,
    formatMoney,
} from './dashboardUtils';

type PropsType = {
    history: RouterHistory,
};

type StateType = {
    loading: boolean,
    error: ?string,
    metrics: any,
    activeDrilldownKey: ?string,
};

class RestaurantActivityDashboard extends Component<PropsType, StateType> {
    constructor(props: PropsType) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            metrics: null,
            activeDrilldownKey: null,
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        this.setState({ loading: true, error: null });
        fetchRestaurantActivityMetrics()
            .then((metrics) => {
                this.setState({ loading: false, metrics, activeDrilldownKey: null });
            })
            .catch((error) => {
                this.setState({ loading: false, error: error.toString() });
            });
    };

    setActiveDrilldown = (drilldownKey: string) => {
        this.setState({ activeDrilldownKey: drilldownKey });
    };

    clearDrilldown = () => {
        this.setState({ activeDrilldownKey: null });
    };

    onSummaryCardKeyDown = (event: SyntheticKeyboardEvent<HTMLElement>, drilldownKey: string) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.setActiveDrilldown(drilldownKey);
        }
    };

    getActiveCard() {
        const { metrics, activeDrilldownKey } = this.state;

        if (!metrics || !activeDrilldownKey) {
            return null;
        }

        return metrics.summaryCards.find((card) => card.drilldownKey === activeDrilldownKey) || null;
    }

    getDrilldownRows() {
        const { metrics, activeDrilldownKey } = this.state;
        const rows = metrics.locationDetails || [];

        switch (activeDrilldownKey) {
            case 'locationsInRotation':
                return rows.filter((row) => row.enabled);
            case 'recentRevenueSampled':
                return rows.filter((row) => row.recentRevenue > 0);
            case 'openBillsAcrossRestaurants':
                return rows.filter((row) => row.openBills > 0);
            case 'staleOpenBillsByLocation':
                return rows.filter((row) => row.staleOpenBills > 0);
            case 'paymentIssuesByLocation':
                return rows.filter((row) => row.attentionTransactions > 0);
            case 'guestDistressByLocation':
                return rows.filter((row) => row.guestDistressSignals > 0);
            default:
                return [];
        }
    }

    renderSummaryCards() {
        const { metrics, activeDrilldownKey } = this.state;
        return (
            <Row>
                {metrics.summaryCards.map((card) => (
                    <Col md="6" xl="3" key={card.label} className="bottom-spaced-element">
                        <Card
                            className={`dashboard-card dashboard-card-action tone-${card.tone || 'neutral'} ${activeDrilldownKey === card.drilldownKey ? 'is-active' : ''}`}
                            data-testid={`restaurant-summary-card-${card.drilldownKey}`}
                            onClick={() => this.setActiveDrilldown(card.drilldownKey)}
                            onKeyDown={(event) => this.onSummaryCardKeyDown(event, card.drilldownKey)}
                            role="button"
                            tabIndex="0"
                        >
                            <CardBody>
                                <div className="dashboard-card-label">{card.label}</div>
                                <div className="dashboard-card-value">{formatMetricValue(card)}</div>
                                <div className="dashboard-card-help">{card.help}</div>
                                <div className="dashboard-card-cta">View locations</div>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>
        );
    }

    renderLocationTable(rows, testId) {
        const { history } = this.props;

        return rows.length === 0 ? (
            <div className="dashboard-empty-state">No locations match the selected metric in the latest sample.</div>
        ) : (
            <Table responsive hover className="dashboard-table" data-testid={testId}>
                <thead>
                    <tr>
                        <th>Location</th>
                        <th>Organization</th>
                        <th>Recent revenue</th>
                        <th>Completed bills</th>
                        <th>Live open bills</th>
                        <th>Stale open bills</th>
                        <th>Approved tx</th>
                        <th>Attention tx</th>
                        <th>Guest risk</th>
                        <th>Rewards redeemed</th>
                        <th>Needs help</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr
                            key={row.id}
                            onClick={() => history.push(`/org_location-info/${row.id}`)}
                            style={{ cursor: 'pointer' }}
                        >
                            <td>
                                <div>{row.name}</div>
                                <div className="dashboard-inline-meta">
                                    Merchant {row.merchantNo || 'Pending'}
                                </div>
                            </td>
                            <td>{row.organizationName}</td>
                            <td>{formatMoney(row.recentRevenue)}</td>
                            <td>{formatCount(row.completedBills)}</td>
                            <td>{formatCount(row.openBills)}</td>
                            <td>
                                <Badge color={row.staleOpenBills > 0 ? 'warning' : 'secondary'}>
                                    {formatCount(row.staleOpenBills)}
                                </Badge>
                            </td>
                            <td>{formatCount(row.approvedTransactions)}</td>
                            <td>
                                <Badge color={row.attentionTransactions > 0 ? 'danger' : 'success'}>
                                    {formatCount(row.attentionTransactions)}
                                </Badge>
                            </td>
                            <td>
                                <Badge color={row.guestDistressSignals > 0 ? 'danger' : 'secondary'}>
                                    {formatCount(row.guestDistressSignals)}
                                </Badge>
                            </td>
                            <td>{formatMoney(row.rewardsRedeemed)}</td>
                            <td>
                                <Badge color={row.assistanceBills > 0 ? 'warning' : 'secondary'}>
                                    {formatCount(row.assistanceBills)}
                                </Badge>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    }

    renderDrilldownPanel() {
        const activeCard = this.getActiveCard();

        return (
            <Card className="dashboard-panel bottom-spaced-element" data-testid="restaurant-drilldown-panel">
                <CardHeader className="dashboard-panel-header dashboard-panel-header-with-action">
                    <div>
                        <div>{activeCard ? `${activeCard.label} details` : 'Metric drill-down'}</div>
                        <div className="dashboard-panel-subtitle">
                            {activeCard ? activeCard.help : 'Select a summary card to reveal the matching restaurant rows.'}
                        </div>
                    </div>
                    {activeCard ? (
                        <Button color="secondary" outline size="sm" onClick={this.clearDrilldown}>
                            Clear
                        </Button>
                    ) : null}
                </CardHeader>
                <CardBody>
                    {activeCard
                        ? this.renderLocationTable(this.getDrilldownRows(), `restaurant-drilldown-table-${activeCard.drilldownKey}`)
                        : <div className="dashboard-empty-state">Choose a summary card above to drill into the matching locations.</div>}
                </CardBody>
            </Card>
        );
    }

    renderTable() {
        const { metrics } = this.state;
        return (
            <Card className="dashboard-panel" data-testid="restaurant-activity-panel">
                <CardHeader className="dashboard-panel-header">Location performance monitor</CardHeader>
                <CardBody>
                    {metrics.locationRows.length === 0 ? (
                        <div className="dashboard-empty-state">No restaurant activity is available yet.</div>
                    ) : (
                        this.renderLocationTable(metrics.locationRows, 'restaurant-activity-table')
                    )}
                </CardBody>
            </Card>
        );
    }

    render() {
        const { loading, error, metrics } = this.state;

        return (
            <div className="operations-page" data-testid="restaurant-activity-page">
                <div className="operations-hero compact">
                    <div>
                        <div className="operations-kicker">Restaurant portal</div>
                        <h1 className="operations-title">Restaurant activity</h1>
                        <p className="operations-subtitle">
                            Track live restaurant performance, live open bills, payment risk, and loyalty impact by location.
                        </p>
                    </div>
                </div>
                {loading ? <div className="dashboard-loading-state"><Spinner color="primary" /></div> : null}
                {error ? <Alert color="danger">{error}</Alert> : null}
                {!loading && !error && metrics ? (
                    <div>
                        {this.renderSummaryCards()}
                        {this.renderDrilldownPanel()}
                        {this.renderTable()}
                    </div>
                ) : null}
            </div>
        );
    }
}

export default withRouter(RestaurantActivityDashboard);